import type { GameVersion, LobbyStatusResponse, SseEvent } from "@redchain/contracts";
import { isApiError, type ApiClient } from "../api/client";
import { sseBus } from "../api/events";
import type { ActiveLobby } from "../link/activeLobby";
import type { LinkEndOutcome, LinkStatus } from "../worker/protocol";
import { romForVersion } from "./db";
import type { EmulatorHost } from "./EmulatorHost";
import type { SessionClient } from "./SessionClient";

export type LinkResult = Extract<SseEvent, { type: "link.result" }>;

/** What the link status bar shows (§17.2 / §17.2.1). */
export type LinkView =
  | { kind: "idle" }
  /** Waiting for the forced save at the Cable Club desk. */
  | { kind: "armed"; lobby: ActiveLobby }
  | { kind: "binding"; lobby: ActiveLobby }
  | { kind: "verifying"; lobby: ActiveLobby; opponentJoined: boolean }
  | { kind: "connecting"; lobby: ActiveLobby }
  | { kind: "handshake"; lobby: ActiveLobby; stateLoaded: boolean }
  | { kind: "running"; lobby: ActiveLobby; side: "A" | "B"; inputDelay: number; stalled: boolean; desyncFrame: number | null }
  | { kind: "ended"; lobby: ActiveLobby; outcome: LinkEndOutcome; reason: string; needsReload: boolean; desyncFrame: number | null }
  | { kind: "error"; lobby: ActiveLobby | null; message: string };

const POLL_MS = 2_000;
const CHECKPOINT_UPLOAD_TIMEOUT_MS = 120_000;

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

function messageOf(e: unknown): string {
  if (isApiError(e)) return e.body.message ?? e.body.reason;
  return e instanceof Error ? e.message : String(e);
}

/**
 * Main-thread side of a link session (WEB-20): arms the worker for the Cable Club save, binds
 * that checkpoint to the lobby, waits until the server has verified both states, then hands
 * the worker everything it needs to open /link/ws. Also matches `link.result` SSE events.
 */
export class LinkController {
  private view: LinkView = { kind: "idle" };
  private result: LinkResult | null = null;
  private lobby: ActiveLobby | null = null;
  private linkSessionId: string | null = null;
  private prep: AbortController | null = null;
  private readonly offSse: () => void;

  constructor(
    private readonly host: EmulatorHost,
    private readonly session: SessionClient,
    private readonly api: ApiClient,
    private readonly ownVersion: GameVersion,
    private readonly onChange: (view: LinkView, result: LinkResult | null) => void,
  ) {
    host.onLink = (s) => this.onStatus(s);
    this.offSse = sseBus.on("link.result", (ev) => {
      const mine = (this.lobby && ev.lobbyId === this.lobby.lobbyId) || (this.linkSessionId && ev.linkSessionId === this.linkSessionId);
      if (!mine) return;
      this.result = ev;
      this.emit();
    });
  }

  get current(): LinkView {
    return this.view;
  }

  /** Busy from arming until the segment ends: power cycles and a second lobby wait. */
  get active(): boolean {
    return this.view.kind !== "idle" && this.view.kind !== "ended" && this.view.kind !== "error";
  }

  arm(lobby: ActiveLobby): void {
    if (this.active) return;
    if (this.session.offline || !this.session.sessionId) {
      this.set({ kind: "error", lobby, message: "Link play needs a connection to the server." });
      return;
    }
    this.lobby = lobby;
    this.result = null;
    this.linkSessionId = null;
    this.host.linkArm({ lobbyId: lobby.lobbyId, hostMode: lobby.hostMode });
  }

  cancel(): void {
    this.prep?.abort();
    this.prep = null;
    this.host.linkCancel();
  }

  /** Clears an ended or failed session from the bar. */
  dismiss(): void {
    if (this.active) return;
    this.lobby = null;
    this.result = null;
    this.set({ kind: "idle" });
  }

  dispose(): void {
    this.prep?.abort();
    this.offSse();
    this.host.onLink = () => {};
  }

  private onStatus(s: LinkStatus): void {
    const lobby = this.lobby;
    switch (s.phase) {
      case "armed":
        if (lobby) this.set({ kind: "armed", lobby });
        return;
      case "paused":
        if (!lobby || lobby.lobbyId !== s.lobbyId) return this.host.linkCancel();
        this.set({ kind: "binding", lobby });
        void this.prepare(lobby, s.checkpointFrame);
        return;
      case "connecting":
        if (lobby) this.set({ kind: "connecting", lobby });
        return;
      case "handshake":
        if (lobby) this.set({ kind: "handshake", lobby, stateLoaded: s.stateLoaded });
        return;
      case "running": {
        if (!lobby) return;
        this.linkSessionId = s.linkSessionId;
        const desyncFrame = this.view.kind === "running" ? this.view.desyncFrame : null;
        this.set({ kind: "running", lobby, side: s.side, inputDelay: s.inputDelay, stalled: s.stalled, desyncFrame });
        return;
      }
      case "desync":
        if (this.view.kind === "running") this.set({ ...this.view, desyncFrame: s.frame });
        return;
      case "ended": {
        if (!lobby) return;
        const desyncFrame = this.view.kind === "running" ? this.view.desyncFrame : null;
        this.set({ kind: "ended", lobby, outcome: s.outcome, reason: s.reason, needsReload: s.needsReload, desyncFrame });
        return;
      }
      case "cancelled":
        // A failed preparation cancels the worker side too; keep its error on screen.
        if (this.view.kind !== "error") {
          this.lobby = null;
          this.set({ kind: "idle" });
        }
        return;
      case "error":
        this.set({ kind: "error", lobby, message: s.message });
        return;
    }
  }

  /** §17.2 steps 2–3: bind the Cable Club checkpoint, wait for both verified states, connect. */
  private async prepare(lobby: ActiveLobby, checkpointFrame: bigint): Promise<void> {
    this.prep?.abort();
    const ac = new AbortController();
    this.prep = ac;
    try {
      const checkpointId = await this.session.checkpointIdAt(checkpointFrame, CHECKPOINT_UPLOAD_TIMEOUT_MS, ac.signal);
      await this.api.bindLinkCheckpoint(lobby.lobbyId, checkpointId);
      this.set({ kind: "verifying", lobby, opponentJoined: false });

      let status: LobbyStatusResponse;
      for (;;) {
        status = await this.api.lobby(lobby.lobbyId);
        if (ac.signal.aborted) return;
        if (status.status === "ended" || status.status === "aborted" || status.status === "desynced") {
          throw new Error("The lobby closed before the link started.");
        }
        this.set({ kind: "verifying", lobby, opponentJoined: status.opponentJoined });
        if (status.statesReady && status.opponentJoined) break;
        await sleep(POLL_MS, ac.signal);
      }

      const [playerId, opponentRom, token] = await Promise.all([
        this.selfPlayerId(),
        lobby.hostMode === "lockstep" ? this.opponentRom(status) : Promise.resolve(null),
        this.api.token ? Promise.resolve(this.api.token) : this.api.refresh(),
      ]);
      if (ac.signal.aborted) return;
      if (!token) throw new Error("You're signed out. Log in again to link.");
      const sessionId = this.session.sessionId;
      if (!sessionId) throw new Error("The game session ended.");
      this.host.linkConnect({ url: this.api.linkWsUrl(lobby.lobbyId), token, lobbyId: lobby.lobbyId, playerId, sessionId, opponentRom });
    } catch (e) {
      if (ac.signal.aborted || (e instanceof DOMException && e.name === "AbortError")) return;
      this.set({ kind: "error", lobby, message: messageOf(e) });
      // Resume solo play from the paused Cable Club save.
      this.host.linkCancel();
    } finally {
      if (this.prep === ac) this.prep = null;
    }
  }

  /**
   * HELLO carries `players.id`, which no typed response exposes yet; read it from /players/me if the
   * API sends it (needs `playerId` on PlayerMe in @redchain/contracts).
   */
  private async selfPlayerId(): Promise<string> {
    const me = await this.api.request<Record<string, unknown>>("GET", "/players/me");
    const id = me.playerId ?? me.id;
    if (typeof id !== "string" || !id) throw new Error("The server didn't report your player id, so the link can't start.");
    return id;
  }

  /**
   * Lockstep runs the opponent's Game Boy here too, on their ROM (§17.2). The lobby doesn't say which
   * version the opponent plays yet; without `opponentGameVersion` both are assumed to match.
   */
  private async opponentRom(status: LobbyStatusResponse): Promise<Uint8Array | null> {
    const v = status.opponentGameVersion;
    if (!v || v === this.ownVersion) return null;
    const rec = await romForVersion(v);
    if (!rec) {
      const name = v === "RED" ? "Pokémon Red" : "Pokémon Blue";
      throw new Error(`Your opponent plays ${name}. Add that ROM on this device, or use a server-run lobby.`);
    }
    return rec.bytes.slice();
  }

  private set(v: LinkView): void {
    this.view = v;
    this.emit();
  }

  private emit(): void {
    this.onChange(this.view, this.result);
  }
}
